import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Loader2, ShieldCheck, Zap, Ban } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import {
  initiateCheckout,
  getPaypalConfig,
  createPaypalOrder,
  capturePaypalOrder,
  persistUtm,
  LOGO_URL,
} from "@/lib/checkout";
import { trackEvent, trackBeginCheckout } from "@/lib/analytics";
import SiteFooter from "@/components/SiteFooter";

const BENEFITS = [
  "One clear priority a day, not a 30-item list that shames you",
  "Plan around the energy you actually have, not the energy you wish you had",
  "A Brain Dump inbox so nothing important slips through",
  "A daily reset that expects a bad day and helps you restart",
];

const TRUST = [
  { Icon: ShieldCheck, text: "30-day money-back guarantee" },
  { Icon: Zap, text: "Instant access after checkout" },
  { Icon: Ban, text: "$17 one time purchase, no subscription" },
];

const PayPalPay = ({ emailRef, setError }) => {
  const [config, setConfig] = useState(null);
  const navigate = useNavigate();
  const invalidRef = useRef(false);
  useEffect(() => {
    getPaypalConfig().then(setConfig).catch(() => setConfig(null));
  }, []);
  if (!config || !config.client_id) return null;
  const emailValid = () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailRef.current || "");
  return (
    <div className="mt-3" role="group" aria-label="Pay with PayPal" data-testid="getstarted-paypal-wrap">
      <PayPalScriptProvider options={{ "client-id": config.client_id, currency: config.currency || "USD", intent: "capture" }}>
        <PayPalButtons
          style={{ layout: "horizontal", color: "gold", shape: "pill", label: "paypal", tagline: false, height: 48 }}
          createOrder={async () => {
            if (!emailValid()) {
              setError("Please enter a valid email address first.");
              invalidRef.current = true;
              throw new Error("invalid_email");
            }
            setError("");
            invalidRef.current = false;
            const orderId = await createPaypalOrder(emailRef.current);
            trackBeginCheckout({ method: "paypal" });
            return orderId;
          }}
          onApprove={async (data) => {
            const result = await capturePaypalOrder(data.orderID);
            if (result.access_token) {
              const params = new URLSearchParams({
                token: result.access_token,
                email: result.email || emailRef.current || "",
                slug: result.product_slug || "",
                amount: String(result.amount_total || 1700),
              });
              navigate(`/thank-you?${params.toString()}`);
            } else {
              setError("Payment captured but no access link returned. Check your email.");
            }
          }}
          onError={() => {
            if (invalidRef.current) { invalidRef.current = false; return; }
            setError("Something went wrong with PayPal. Please try again or use card.");
          }}
        />
      </PayPalScriptProvider>
    </div>
  );
};

export default function GetStarted() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const emailRef = useRef("");

  useEffect(() => {
    persistUtm();
    trackEvent("page_view", { page: "get_started" });
  }, []);

  const onChange = (e) => {
    setEmail(e.target.value);
    emailRef.current = e.target.value;
    if (error) setError("");
  };

  const buy = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await initiateCheckout(email);
    } catch (err) {
      setError("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#1F2430]">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-[#0B132B] focus:px-4 focus:py-2 focus:text-white"
      >
        Skip to main content
      </a>

      <header className="border-b border-[#E6E9EE] bg-white">
        <div className="mx-auto flex max-w-[1000px] items-center justify-between px-6 py-4">
          <Link to="/" aria-label="CommandCenterADHD home">
            <img src={LOGO_URL} alt="CommandCenterADHD" width="640" height="316" className="h-9 w-auto object-contain" />
          </Link>
          <span className="font-heading text-sm font-bold text-[#15816B]">$17 one time purchase</span>
        </div>
      </header>

      <main id="main">
        <section className="px-6 pt-14 pb-10 md:pt-20" aria-labelledby="gs-hero-heading">
          <div className="mx-auto max-w-[760px] text-center">
            <h1 id="gs-hero-heading" className="font-heading text-4xl font-extrabold leading-tight tracking-tight text-[#0B132B] sm:text-5xl">
              Start here when your brain feels overwhelmed.
            </h1>
            <p className="mx-auto mt-5 max-w-[560px] text-lg leading-relaxed text-[#3A4252]">
              Get the full CommandCenterADHD Notion system: one priority, honest time blocks,
              a brain dump inbox, and a reset built for the days the wheels come off.
            </p>

            <form onSubmit={buy} noValidate className="mx-auto mt-8 max-w-[440px] text-left">
              <label htmlFor="gs-email" className="block text-sm font-semibold text-[#0B132B]">
                Your email
              </label>
              <input
                id="gs-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="name@email.com"
                value={email}
                onChange={onChange}
                aria-invalid={!!error}
                aria-describedby={error ? "gs-email-error" : "gs-email-help"}
                data-testid="getstarted-email-input"
                className="mt-1.5 h-12 w-full rounded-xl border border-[#C9D0D9] bg-white px-4 text-base text-[#0B132B] outline-none focus-visible:border-[#15816B] focus-visible:ring-2 focus-visible:ring-[#15816B]/40"
              />
              {error ? (
                <p id="gs-email-error" role="alert" className="mt-2 text-sm font-medium text-[#B02A1E]" data-testid="getstarted-error">
                  {error}
                </p>
              ) : (
                <p id="gs-email-help" className="mt-2 text-sm text-[#5A6472]">
                  We email your access link here right after checkout.
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="getstarted-buy-button"
                className="mt-4 inline-flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-[#15816B] px-6 py-3.5 font-heading text-base font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0B132B] focus-visible:ring-offset-2 disabled:opacity-70"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : "Get instant access for $17"}
              </button>

              <div className="mt-4 text-center">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#5A6472]">or pay with</p>
                <PayPalPay emailRef={emailRef} setError={setError} />
              </div>
            </form>

            <ul className="mx-auto mt-8 flex max-w-[600px] flex-wrap items-center justify-center gap-x-6 gap-y-3">
              {TRUST.map(({ Icon, text }) => (
                <li key={text} className="flex items-center gap-2 text-sm font-medium text-[#3A4252]">
                  <Icon className="h-4 w-4 text-[#15816B]" aria-hidden="true" />
                  {text}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="px-6 py-4" aria-labelledby="gs-what-heading">
          <div className="mx-auto max-w-[900px] overflow-hidden rounded-2xl border border-[#E6E9EE] bg-white p-6 shadow-[0_16px_40px_-24px_rgba(11,19,43,0.35)] md:grid md:grid-cols-2 md:gap-8 md:p-8">
            <div>
              <h2 id="gs-what-heading" className="font-heading text-2xl font-bold text-[#0B132B]">
                What you get
              </h2>
              <ul className="mt-5 space-y-3">
                {BENEFITS.map((b) => (
                  <li key={b} className="flex gap-3 text-[15px] leading-relaxed text-[#3A4252]">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#15816B]" aria-hidden="true" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-6 overflow-hidden rounded-xl border border-[#EEF1F5] bg-[#0B132B] md:mt-0" style={{ aspectRatio: "16 / 9" }}>
              <img
                src="/proof-command-center.jpg"
                alt="The CommandCenterADHD daily dashboard showing one priority, task views and a reset button"
                loading="lazy"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        <section className="px-6 py-12" aria-labelledby="gs-guarantee-heading">
          <div className="mx-auto max-w-[640px] rounded-2xl border border-[#F5A623]/40 bg-white p-7 text-center">
            <h2 id="gs-guarantee-heading" className="font-heading text-xl font-bold text-[#0B132B]">
              Try it for 30 days
            </h2>
            <p className="mx-auto mt-3 max-w-[500px] text-[15px] leading-relaxed text-[#3A4252]">
              If it does not fit your routine, email{" "}
              <a href="mailto:support@commandcenteradhd.com" className="font-semibold text-[#15816B] underline">
                support@commandcenteradhd.com
              </a>{" "}
              within 30 days for a full refund. See our{" "}
              <Link to="/refund-policy" className="font-semibold text-[#15816B] underline">refund policy</Link>.
            </p>
            <a
              href="#main"
              onClick={(e) => { e.preventDefault(); document.getElementById("gs-email")?.focus(); document.getElementById("gs-email")?.scrollIntoView({ behavior: "smooth", block: "center" }); }}
              className="mt-6 inline-flex items-center justify-center rounded-xl bg-[#0B132B] px-6 py-3 font-heading text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#15816B] focus-visible:ring-offset-2"
              data-testid="getstarted-secondary-cta"
            >
              Get instant access for $17
            </a>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
